import React, { createContext, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  createIVRSession,
  getIVRSessionData,
  finalizeFolio,
  closeFolio,
  getCustomerId,
  openFolioByGuest,
  openFolioByRegistered,
} from '@csc-dashboard/api-client';
import {
  AdminPanelProvider,
  AdminPanel,
  ScopedModal,
  AlertProvider,
  useAdminPanel,
  useAlert,
} from '@csc-dashboard/admin-panel';
import { AtIcon, cn } from '@csc-dashboard/components-library'
import commonUtil from '../../utils/commonUtil';

export const VISA = 'VISA';
export const AMEX = 'AMEX';
export const DEPARTAMENTAL = 'DEPARTAMENTAL';
export const DIGITAL_WALLET = 'DIGITAL_WALLET';

export const CARD_TYPES = {
  'American Express': AMEX,
  'Fabricas de Francia': DEPARTAMENTAL,
  'Suburbia Departamental': DEPARTAMENTAL,
  'Liverpool Garantizada': DEPARTAMENTAL,
  'Liverpool Just me': VISA,
  'Suburbia Garantizada': DEPARTAMENTAL,
  'Liverpool Departamental': DEPARTAMENTAL,
  'Liverpool Visa': VISA,
  'Monedero Corporativo': DIGITAL_WALLET,
  Visa: VISA,
  'Mastercard - Carnet': VISA,
  'Tarjetas de vales Pluxee': VISA,
  'Suburbia Visa': VISA,
};

export const ImpersonationContext = createContext({
  folioId: null,
  isImpersonation: false,
  isGuest: false,
  handleImpersonationLogout: () => {},
  startIVRCall: () => Promise.resolve({ cardType: 'credit_card', cardLastFourDigits: '', status: 'error' }),
  lastIvrResult: null,
  lastIvrCardId: null,
  setLastIvrCardId: () => {},
  handleOpenFolio: () => Promise.resolve(''),
});

// ---------------------------------------------------------------------------
// Alert sub-components (must be rendered inside AdminPanelProvider + AlertProvider)
// ---------------------------------------------------------------------------

const NewFolioImpersonationAlert = ({ isNewFolio }) => {
  const { agentName } = useAdminPanel();
  const { folioId } = useImpersonation();
  const { showSuccess } = useAlert();

  useEffect(() => {
    if (folioId && agentName.length) {
      const message = isNewFolio ? (
        <>
          El folio {folioId} de {agentName} se ha creado y está ahora{' '}
          <span style={{ fontFamily: 'Roboto-Bold', fontWeight: '700' }}>activo</span>.
        </>
      ) : (
        <>
          El folio {folioId} de {agentName} está ahora{' '}
          <span style={{ fontFamily: 'Roboto-Bold', fontWeight: '700' }}>activo</span>.
        </>
      )

      showSuccess(message)
    }
  }, [agentName, folioId]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const FinalizeOrderImpersonationAlert = ({ action }) => {
  const { folioId } = useImpersonation();
  const { showSuccess } = useAlert();

  useEffect(() => {
    showSuccess(`El folio ${folioId} se ha marcado como cerrado.`, {
      action,
      buttonText: 'Finalizar atención',
      backdropClose: false,
      autoClose: false,
      buttonClassName: 'w-[120px] !py-2 !text-xs',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const InvalidSessionAlert = ({ action }) => {
  const { showWarning } = useAlert();

  useEffect(() => {
    showWarning('La sesión ha expirado.', {
      action,
      buttonText: 'Iniciar nueva sesión',
      backdropClose: false,
      autoClose: false,
      buttonClassName: 'w-[120px] !py-2 !text-xs',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ---------------------------------------------------------------------------
// ImpersonationProvider
// ---------------------------------------------------------------------------

export function ImpersonationProvider({ children, user }) {
  const [folioId] = useState(() =>
    typeof document !== 'undefined' ? commonUtil.readCookie('x-cs-folio-id') || null : null,
  );
  const [isNewFolio] = useState(() =>
    typeof document !== 'undefined' ? commonUtil.readCookie('is-new-folio') || null : null,
  );
  const [isNewImpersonationSession] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('newImpersonationSession') === 'true';
  });

  const [isGuest, setIsGuest] = useState(true);
  const [isloadingUserInfo, setIsloadingUserInfo] = useState(false);
  const [isRetryingSession, setIsRetryingSession] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isIVRLoading, setIsIVRLoading] = useState(false);
  const [lastIvrCardId, setLastIvrCardId] = useState(null);
  const [lastIvrResult, setLastIvrResult] = useState(null);
  const [isFinalizeOrderImpersonation, setIsFinalizeOrderImpersonation] = useState(false);
  const [ivrOrigin, setIvrOrigin] = useState(null);
  const [isInvalidSession, setIsInvalidSession] = useState(false);

  const handleRedirect = async (orderFinalized = false) => {
    const cscAdminURL = process.env.NEXT_PUBLIC_CSC_REDIRECT;
    if (!cscAdminURL) {
      console.error('[AgentPanel] NEXT_PUBLIC_CSC_REDIRECT is not configured.');
      return;
    }

    try {
      const url = new URL(cscAdminURL);
      const dashboardPath = '/dashboard/inicio';
      url.pathname = url.pathname.length > 1 ? url.pathname + dashboardPath : dashboardPath;

      if (orderFinalized) {
        try {
          await closeFolio({ folioId: folioId || '' });
          url.searchParams.set('isOrderFinalized', 'true');
        } catch (error) {
          console.error('[AgentPanel] closeFolio error:', error);
          return;
        }
      }

      url.searchParams.set('folioId', folioId || '');
      commonUtil.deleteCookie('x-cs-folio-id');
      commonUtil.deleteCookie('is-new-folio');
      window.location.href = url.toString();
    } catch (error) {
      console.error('[AgentPanel] handleRedirect error:', error);
    }
  };

  const handleLogout = async () => {
    if (!folioId) {
      handleRedirect();
      return;
    }
    try {
      await finalizeFolio({ folioId });
      handleRedirect();
    } catch (error) {
      console.error('[AgentPanel] finalizeFolio error:', error);
    }
  };

  const handleOpenFolio = async (brand) => {
    if (!folioId) return '';
    try {
      const { customerId } = await getCustomerId({ folioId });
      let newFolioId;
      if (isGuest) {
        const response = await openFolioByGuest({ brand });
        newFolioId = response.folioId;
      } else {
        const response = await openFolioByRegistered({ customerId, forceOpen: true, brand });
        newFolioId = response.id;
      }
      return newFolioId;
    } catch (error) {
      console.error('[AgentPanel] handleOpenFolio error:', error);
      return '';
    }
  };

  const startIVRCall = async (props) => {
    const action = props?.action || 'add';
    const cardType = action === 'add' ? (CARD_TYPES[props.ivrCardType] || props.ivrCardType) : props.ivrCardType;

    try {
      const sessionPayload = {
        action,
        folioId: folioId || '',
        type: cardType,
        paymentMethods:
          props?.action === 'select'
            ? { cards: [{ cardNumber: props?.cardId || '' }] }
            : undefined,
      };

      setIsIVRLoading(true);
      setIvrOrigin(props?.origin || 'my-account');

      const ivrSession = await createIVRSession(sessionPayload);
      const sessionData = await getIVRSessionData({ sessionId: ivrSession });
      const { data } = sessionData;
      const { closureReason } = data;

      if (closureReason === 'COMPLETED') {
        const { cardLastFourDigits, cardType: resultCardType } = data.result;
        if (props?.action === 'select') {
          setLastIvrCardId(props?.cardId || null);
        }
        const result = {
          status: 'completed',
          ivrSessionId: ivrSession,
          cardLastFourDigits,
          cardType: resultCardType,
        };
        setLastIvrResult(result);
        return result;
      }

      return { status: 'error' };
    } catch (error) {
      console.error('[AgentPanel] startIVRCall error:', error);
      return { status: 'error' };
    } finally {
      setIsIVRLoading(false);
    }
  };

  // Fetch customer info to determine guest status
  useEffect(() => {
    const fetchUserInfo = async () => {
      setIsloadingUserInfo(true);
      try {
        if (user) {
          setUserInfo({
            name: user.firstName + ' ' + user.lastName,
            email: user.emailId ?? '',
            birthDay: user.dob ?? '',
            gender: user.gender ? user.gender == "F" ? "Mujer" : "Hombre" : "",
            registeredAt: user?.createdAt?.split(' ')[0] ?? '',
          })
          setIsGuest(false);
        } else {
          setIsGuest(true);
        }
      } catch (error) {
        if (error?.status === 403) setIsInvalidSession(true);
        setIsGuest(true);
      } finally {
        setIsloadingUserInfo(false);
      }
    };

    if (folioId && !userInfo) {
      fetchUserInfo();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect checkout confirmation page to trigger finalize alert
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('checkoutOrderConfirmation')) {
      setIsFinalizeOrderImpersonation(true);
    }
  }, []);

  // Handle invalid session — silent auth is not available in this codebase;
  // set IAEL cookie to prevent repeat attempts and mark session retry as done.
  useEffect(() => {
    if (isInvalidSession && folioId) {
      const IAEL = commonUtil.readCookie('IAEL');
      if (!IAEL || IAEL !== folioId) {
        commonUtil.createCookie('IAEL', folioId, null, '/');
      }
    }
    setIsRetryingSession(false);
  }, [isInvalidSession, folioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOriginCheckout = ivrOrigin === 'checkout';

  return (
    <ImpersonationContext.Provider
      value={{
        folioId,
        isGuest,
        handleImpersonationLogout: handleLogout,
        isImpersonation: !!folioId,
        startIVRCall,
        lastIvrCardId,
        setLastIvrCardId,
        lastIvrResult,
        handleOpenFolio,
      }}
    >
      <AlertProvider>
        {folioId && (
          <AdminPanelProvider
            initialState={{
              folioId,
              hideAlert: false,
              isloadingUserInfo,
              userInfo: userInfo || undefined,
              isGuest,
              onAttentionFinished: handleRedirect,
            }}
          >
            {isNewImpersonationSession && <NewFolioImpersonationAlert isNewFolio={isNewFolio} />}
            {isFinalizeOrderImpersonation && (
              <FinalizeOrderImpersonationAlert action={() => handleRedirect(true)} />
            )}
            {isInvalidSession && !isRetryingSession && (
              <InvalidSessionAlert action={handleRedirect} />
            )}

            {!isRetryingSession && <AdminPanel />}

            <ScopedModal
              open={isIVRLoading}
              boxClassName={cn(
                isOriginCheckout ? '!w-screen !h-screen !bg-white/80 flex items-center justify-center' : '',
              )}
            >
              <div
                className={cn(
                  isOriginCheckout ? 'max-w-[420px]' : '',
                  'flex flex-col gap-10 text-center text-surface-500',
                )}
              >
                <div className="flex flex-col gap-6 items-center justify-center">
                  <AtIcon className="!text-prim-500" name="loading" size={isOriginCheckout ? 90 : 48} />
                  <h2 className={cn(isOriginCheckout ? 'font-bold' : '', 'text-2xl')}>
                    Esperando datos del cliente...
                  </h2>
                  <p className="text-lg">El cliente está cargando su información a través del IVR.</p>
                </div>
              </div>
            </ScopedModal>
          </AdminPanelProvider>
        )}
        {children}
      </AlertProvider>
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}

function AgentPanel({ children, user }) {
  return (
    <ImpersonationProvider user={user}>
      {children}
    </ImpersonationProvider>
  );
}

const mapStateToProps = state => ({
  user: state.user && state.user.data && state.user.data.result && state.user.data.result.profileId
    ? state.user.data.result
    : null,
});

export default connect(mapStateToProps)(AgentPanel);
